/**
 * Feedback warp pass. Reads the previous-frame texture, writes the warped
 * result into the current target. Bind groups are recreated whenever the
 * ring rotates (because the input view changes).
 */

import warpWgsl from '../shaders/feedback_warp.wgsl?raw';

/**
 * Mirrors the KIND_ constants in feedback_warp.wgsl. New values must be
 * appended (existing presets pin to specific numbers in the preset registry).
 */
export const enum WarpKind {
  Radial = 0,
  Spiral = 1,
  Sinusoidal = 2,
  LegacyPhase0 = 3,
  Field = 4,
}

export type WarpParams = {
  kind: WarpKind;
  baseZoom: number;
  bassZoomGain: number;
  rotation: number;
  bassRotationGain: number;
  ripple: number;
  rippleFreq: number;
  decay: number;
  chroma: number;
  hueShift: number;
  beatPunch: number;
  centerDriftAmp: number;
  centerDriftRate: number;
  /** WarpFieldSystem crossfade progress, in [0,1]. 1 means no crossfade. */
  fieldT: number;
  /** Multiplier on the displacement vector sampled from the warp field. */
  fieldStrength: number;
  /** Extra decay boost toward 1.0 far from the wave line (fills screen). */
  trailBoost?: number;
};

// 1 u32 + 12 f32 + 3 f32 of pad = 16 * 4 = 64 bytes (16-byte aligned).
const WARP_PARAMS_BYTES = 64;

export class FeedbackWarpPass {
  private readonly device: GPUDevice;
  private readonly pipeline: GPURenderPipeline;
  private readonly layout: GPUBindGroupLayout;
  private readonly paramsBuffer: GPUBuffer;
  private readonly cpuParams = new ArrayBuffer(WARP_PARAMS_BYTES);
  private readonly cpuParamsF32 = new Float32Array(this.cpuParams);
  private readonly cpuParamsU32 = new Uint32Array(this.cpuParams);
  private readonly frameUniformsBuffer: GPUBuffer;

  constructor(
    device: GPUDevice,
    targetFormat: GPUTextureFormat,
    frameUniformsBuffer: GPUBuffer,
  ) {
    this.device = device;
    this.frameUniformsBuffer = frameUniformsBuffer;

    this.paramsBuffer = device.createBuffer({
      label: 'warp-params',
      size: WARP_PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const shader = device.createShaderModule({
      label: 'feedback-warp',
      code: warpWgsl,
    });

    this.layout = device.createBindGroupLayout({
      label: 'warp-bgl',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: 'filtering' },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
        // Warp-field bindings. Always bound; the shader only reads them
        // when warp.kind == Field. Procedural-only presets bind the
        // engine's 2x2 placeholder texture here.
        {
          binding: 4,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
        {
          binding: 5,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: 'feedback-warp-pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.layout] }),
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: {
        module: shader,
        entryPoint: 'fs_main',
        targets: [{ format: targetFormat }],
      },
      primitive: { topology: 'triangle-list' },
    });
  }

  updateParams(p: WarpParams): void {
    const f = this.cpuParamsF32;
    const u = this.cpuParamsU32;
    u[0] = p.kind >>> 0;
    f[1] = p.baseZoom;
    f[2] = p.bassZoomGain;
    f[3] = p.rotation;
    f[4] = p.bassRotationGain;
    f[5] = p.ripple;
    f[6] = p.rippleFreq;
    f[7] = p.decay;
    f[8] = p.chroma;
    f[9] = p.hueShift;
    f[10] = p.beatPunch;
    f[11] = p.centerDriftAmp;
    f[12] = p.centerDriftRate;
    f[13] = p.fieldT;
    f[14] = p.fieldStrength;
    f[15] = p.trailBoost ?? 0;
    this.device.queue.writeBuffer(this.paramsBuffer, 0, this.cpuParams);
  }

  /**
   * Build a fresh bind group for the current ring orientation. Field views
   * are always provided -- when no WarpFieldSystem is active for a preset,
   * pass the engine's placeholder view in both slots.
   */
  makeBindGroup(
    prevView: GPUTextureView,
    sampler: GPUSampler,
    fieldCurrView: GPUTextureView,
    fieldPrevView: GPUTextureView,
  ): GPUBindGroup {
    return this.device.createBindGroup({
      label: 'warp-bg',
      layout: this.layout,
      entries: [
        { binding: 0, resource: { buffer: this.frameUniformsBuffer } },
        { binding: 1, resource: prevView },
        { binding: 2, resource: sampler },
        { binding: 3, resource: { buffer: this.paramsBuffer } },
        { binding: 4, resource: fieldCurrView },
        { binding: 5, resource: fieldPrevView },
      ],
    });
  }

  encode(
    encoder: GPUCommandEncoder,
    targetView: GPUTextureView,
    bindGroup: GPUBindGroup,
  ): void {
    const pass = encoder.beginRenderPass({
      label: 'feedback-warp-pass',
      colorAttachments: [
        {
          view: targetView,
          loadOp: 'clear',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.draw(6, 1, 0, 0);
    pass.end();
  }
}
